// S5 — REMNANT REGISTRY, /waste/remnants (Module 05 §4, slice 05-B).
//
// The corner pile as first-class inventory: one row per §2.6 registry piece,
// `length_m DESC` (the server's matcher-shaped order, never re-sorted), cursor
// pages, the lifecycle actions inline (reserve / release / re-class / scrap —
// §3.4's state machine, each refused server-side per the §5 role narrowings).
//
// THE ≤3-CLICK BAR IS HALF HERE, AND THE SCREEN SAYS WHICH HALF: the search
// bar filters the registry (item, length band, status) — the instant
// exact/single/combination ANSWER is 05-C's match engine, and the bar states
// that rather than shelling a preview that computes nothing (D-033).
//
// No money on this screen at all: §2.6 carries no cost columns, and the price
// CLASS chip (a discount label) is catalog data every §5 role may see.
//
// UX-4 — the table is the §3.3 `DataTable` and the lifecycle panel is its
// per-row EXPANSION (one `<tr colSpan>` directly under the piece it acts on,
// merged into the same card at phone width), which is exactly what the
// hand-rolled `.waste-remnant-actions` row was imitating. Two honesty fixes
// came out of the migration:
//
//   * AN UNRESOLVED PRICE CLASS PRINTED ITS RAW UUID. `className()` fell back
//     to the id whenever the class list had not answered — so a registry row
//     was labelled `0191f3c1-8a4e-…` where a discount name belongs, and it read
//     identically whether the list was still loading or the class had been
//     archived out of it. `RefLabel` separates those.
//   * A PIECE WITH NO LENGTH RENDERED AS AN EMPTY CELL, indistinguishable from
//     a value the screen failed to paint. §2.6 allows a null length (a sheet
//     off-cut has an area, not a length); it now says so.
//
// TASK #105 (2026-08-17) — WHAT A REFUSED WRITE SAYS. The four lifecycle
// mutations shared one OR'd `failed` flag rendering one fixed sentence: no
// verb, no server title, no request id, and no reset — so a 409 on reserve was
// still on the glass after a re-class the server ACCEPTED. The panel now keeps
// per-verb error state on the area-shared `MutationFailureNote` (see its
// header), and a press clears its siblings' stale refusals.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { requestIdOf } from "../../../../lib/request";
import { formatQty, formatTimestamp, localeLabel } from "../../production/lib/format";
import { useProductSearch } from "../../production/hooks/production-queries";
import { MutationFailureNote } from "../mutation-failure-note";
import { isWasteReadRefused, useWastePermissions } from "../hooks/permissions";
import {
  useReclassRemnant,
  useReleaseRemnant,
  useRemnantPriceClasses,
  useRemnants,
  useReserveRemnant,
  useScrapRemnant,
  type Remnant,
  type RemnantsQuery,
} from "../hooks/waste-queries";

/**
 * §2.6's status vocabulary → the §3.5 `StatusChip` variant, stated once.
 *
 * `available` is GREEN (the piece is money the plant can still use),
 * `reserved` is ACCENT (spoken for — current, not finished), `scrapped` is RED
 * (the value was thrown away), and `consumed`/`sold` are NEUTRAL: both are
 * successful ends of the piece's life, and painting them green would put the
 * registry's two archival states in competition with its one actionable one.
 */
export function remnantStatusVariant(status: string): ChipVariant {
  switch (status) {
    case "available":
      return "ok";
    case "reserved":
      return "accent";
    case "scrapped":
      return "bad";
    default:
      return "neutral";
  }
}

export function WasteRemnantsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useWastePermissions();

  const classes = useRemnantPriceClasses();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [minLength, setMinLength] = useState("");
  const [maxLength, setMaxLength] = useState("");
  // 05-B-2 — the ITEM half of §4's "item picker + required length" bar. The
  // registry may not read module 01's tables, so the SCREEN resolves the SKU
  // through the catalogue's own search API and filters on `item_id` (a §5
  // param that was contract-live with no control). The filter applies only on
  // an EXACT SKU match — a substring that silently filtered by the wrong item
  // would be a false answer to "do we have 100 m of КГ 3×70?".
  const [itemText, setItemText] = useState("");
  const itemSearch = useProductSearch(itemText.trim());
  const itemMatch = useMemo(() => {
    const needle = itemText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (itemSearch.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [itemSearch.data, itemText]);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rowsAccum, setRowsAccum] = useState<readonly Remnant[]>([]);
  const [actionRow, setActionRow] = useState<string | undefined>(undefined);

  const query: RemnantsQuery = useMemo(
    () => ({
      ...(q.length > 0 ? { q } : {}),
      ...(itemMatch !== undefined ? { item_id: itemMatch.id } : {}),
      ...(kind.length > 0 ? { kind } : {}),
      ...(status.length > 0 ? { status } : {}),
      ...(minLength.length > 0 ? { min_length_m: minLength } : {}),
      ...(maxLength.length > 0 ? { max_length_m: maxLength } : {}),
    }),
    [q, itemMatch, kind, status, minLength, maxLength],
  );

  // Keyed on WHAT the query asks, not the memo's identity: `itemMatch` is an
  // object off a search refetch and can renew its identity while naming the
  // same item — an identity-keyed reset then wipes rows already folded, with
  // nothing left to re-fire the fold (the events screen's M05-S08 race,
  // 2026-08-14; same accumulator, same fix).
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setCursors([undefined]);
    setRowsAccum([]);
  }, [queryKey]);

  const refused = isWasteReadRefused(perms, "waste.remnant.manage");
  const cursor = cursors[cursors.length - 1];
  const page = useRemnants(query, cursor, !refused);

  useEffect(() => {
    if (page.data === undefined) return;
    const items = page.data.items;
    // A lifecycle action refreshed the list — fold updated rows over the
    // accumulation by id (lib/paged-rows.ts, the area's one fold rule).
    setRowsAccum((prior) => foldPagedRows(prior, items));
  }, [page.data]);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const classLabel = (id: string): string | undefined => {
    const row = classes.data?.find((c) => c.id === id);
    return row === undefined ? undefined : localeLabel(row.name_i18n, i18n.language) || row.code;
  };

  const canManage = perms.has("waste.remnant.manage");

  const columns: DataTableColumn<Remnant>[] = [
    { key: "remnant_no", header: t("waste.remnants.colNo"), id: true },
    {
      key: "kind",
      header: t("waste.remnants.colKind"),
      render: (row) => t(`waste.remnantKind.${row.kind}`),
    },
    {
      key: "length_m",
      header: t("waste.remnants.colLength"),
      numeric: true,
      // §2.6 allows a null length (a sheet off-cut has an area). Absent says
      // so; it does not render as an empty cell.
      render: (row) => (
        <RefLabel
          value={
            row.length_m === null
              ? undefined
              : `${formatQty(row.length_m, { locale: i18n.language })} m`
          }
          id={row.length_m}
        />
      ),
    },
    {
      key: "diameter_mm",
      header: t("waste.remnants.colDiameter"),
      numeric: true,
      render: (row) => (
        <RefLabel
          value={
            row.diameter_mm === null
              ? undefined
              : `${formatQty(row.diameter_mm, { locale: i18n.language })} mm`
          }
          id={row.diameter_mm}
        />
      ),
    },
    {
      key: "price_class_id",
      header: t("waste.remnants.colClass"),
      render: (row) =>
        row.price_class_id === null ? (
          <StatusChip variant="neutral">{t("waste.remnants.unclassed")}</StatusChip>
        ) : (
          <RefLabel
            value={classLabel(row.price_class_id)}
            id={row.price_class_id}
            pending={classes.isPending}
          />
        ),
    },
    {
      key: "status",
      header: t("waste.remnants.colStatus"),
      render: (row) => (
        <StatusChip variant={remnantStatusVariant(row.status)} data-testid="remnant-status">
          {t(`waste.remnantStatus.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "created_at",
      header: t("waste.remnants.colAge"),
      age: true,
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
    {
      key: "actions",
      header: t("waste.remnants.colActions"),
      actions: true,
      render: (row) =>
        canManage ? (
          <Button
            size="sm"
            data-testid="remnant-actions-toggle"
            aria-expanded={actionRow === row.id}
            onClick={() => {
              setActionRow(actionRow === row.id ? undefined : row.id);
            }}
          >
            {actionRow === row.id ? t("waste.common.close") : t("waste.remnants.actions")}
          </Button>
        ) : null,
    },
  ];

  return (
    <section className="waste-remnants-screen" data-testid="waste-remnants-screen">
      <PageHeader title={t("waste.remnants.title")} />

      <div className="filter-bar" data-testid="waste-remnants-filters">
        <Field label={t("waste.remnants.search")}>
          <input
            value={q}
            placeholder={t("waste.remnants.searchHint")}
            data-testid="remnants-q"
            onChange={(e) => {
              setQ(e.target.value);
            }}
          />
        </Field>
        <Field label={t("waste.remnants.item")} wide>
          <input
            value={itemText}
            placeholder={t("waste.remnants.itemHint")}
            data-testid="remnants-item"
            onChange={(e) => {
              setItemText(e.target.value);
            }}
          />
          {itemText.trim().length > 0 ? (
            itemMatch !== undefined ? (
              <span className="waste-applied" data-testid="remnants-item-applied">
                {t("waste.remnants.itemApplied", { sku: itemMatch.sku })}
              </span>
            ) : (
              <span className="waste-applied" data-testid="remnants-item-pending">
                {t("waste.remnants.itemNoMatch")}
              </span>
            )
          ) : null}
        </Field>
        <Field label={t("waste.remnants.kind")}>
          <select
            value={kind}
            data-testid="remnants-kind"
            onChange={(e) => {
              setKind(e.target.value);
            }}
          >
            <option value="">{t("waste.remnants.anyKind")}</option>
            {(["wire_remnant", "cable_offcut", "sheet_offcut"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`waste.remnantKind.${value}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("waste.remnants.status")}>
          <select
            value={status}
            data-testid="remnants-status"
            onChange={(e) => {
              setStatus(e.target.value);
            }}
          >
            <option value="">{t("waste.remnants.anyStatus")}</option>
            {(["available", "reserved", "consumed", "sold", "scrapped"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`waste.remnantStatus.${value}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("waste.remnants.minLength")}>
          <input
            inputMode="decimal"
            value={minLength}
            data-testid="remnants-min-length"
            onChange={(e) => {
              setMinLength(e.target.value);
            }}
          />
        </Field>
        <Field label={t("waste.remnants.maxLength")}>
          <input
            inputMode="decimal"
            value={maxLength}
            data-testid="remnants-max-length"
            onChange={(e) => {
              setMaxLength(e.target.value);
            }}
          />
        </Field>
      </div>

      {refused ? (
        <ForbiddenState title={t("waste.access.title")} body={t("waste.access.registry")} />
      ) : null}

      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("waste.offline.title")} body={t("waste.offline.body")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("waste.common.loading")} />
      ) : null}

      {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("waste.remnants.error")}
          retryLabel={t("waste.common.retry")}
          requestIdLabel={t("waste.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        <EmptyState title={t("waste.remnants.emptyTitle")} body={t("waste.remnants.emptyBody")} />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          <DataTable
            data-testid="waste-remnants-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            empty={null}
            caption={t("waste.remnants.tableCaption")}
            rowTestId="waste-remnant-row"
            rowProps={(row) => ({ "data-status": row.status })}
            expansionLabel={() => t("waste.remnants.colActions")}
            expansion={(row) =>
              actionRow === row.id && canManage ? <RemnantActions row={row} /> : null
            }
          />

          {page.data?.next_cursor != null ? (
            <div className="waste-toolbar">
              <Button
                size="sm"
                data-testid="waste-remnants-more"
                onClick={() => {
                  const next = page.data?.next_cursor;
                  if (next != null) setCursors((prior) => [...prior, next]);
                }}
              >
                {t("waste.events.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** §3.4's lifecycle actions for ONE piece, in the row's expansion panel. Which
 * of the four are offered is decided by the piece's own status; each is refused
 * server-side per the §5 role narrowings regardless (D-040's two layers). */
function RemnantActions({ row }: { row: Remnant }) {
  const { t, i18n } = useTranslation();
  const classes = useRemnantPriceClasses();
  const reserve = useReserveRemnant();
  const release = useReleaseRemnant();
  const reclass = useReclassRemnant();
  const scrap = useScrapRemnant();

  const [reserveType, setReserveType] = useState<"wo_material_requirement" | "sales_order_line">(
    "wo_material_requirement",
  );
  const [reserveId, setReserveId] = useState("");
  const [classId, setClassId] = useState("");
  const [scrapReason, setScrapReason] = useState("");

  const busy = reserve.isPending || release.isPending || reclass.isPending || scrap.isPending;

  return (
    <>
      {/* PER-VERB refusals, not an OR of four flags (task #105). This used to
          be `const failed = reserve.isError || release.isError || … `, and the
          disjunction had two lies in it: nothing ever RESET a sibling, so a
          refusal from one verb stayed on the glass after a DIFFERENT verb
          succeeded — the registry telling the storekeeper it refused an action
          it had just accepted — and the one anonymous sentence never named
          which of §3.4's four actions was refused. Each verb now renders its
          own note (the sentence names the verb; the shared MutationFailureNote
          adds the server's own title and the request id), and every press
          clears its SIBLINGS' state below — the moment the user asks for a
          different action, the old refusal is no longer the current claim. At
          most one note can exist at a time: the press-time reset plus the
          `busy` gate guarantee it. */}
      {reserve.isError ? (
        <MutationFailureNote
          data-testid="remnant-action-failed"
          text={t("waste.remnants.reserveFailed")}
          error={reserve.error}
        />
      ) : null}
      {release.isError ? (
        <MutationFailureNote
          data-testid="remnant-action-failed"
          text={t("waste.remnants.releaseFailed")}
          error={release.error}
        />
      ) : null}
      {reclass.isError ? (
        <MutationFailureNote
          data-testid="remnant-action-failed"
          text={t("waste.remnants.reclassFailed")}
          error={reclass.error}
        />
      ) : null}
      {scrap.isError ? (
        <MutationFailureNote
          data-testid="remnant-action-failed"
          text={t("waste.remnants.scrapFailed")}
          error={scrap.error}
        />
      ) : null}
      {row.status === "available" ? (
        <div className="waste-drawer-action">
          <Field label={t("waste.remnants.reserveFor")}>
            <select
              value={reserveType}
              data-testid="remnant-reserve-type"
              onChange={(e) => {
                setReserveType(e.target.value as typeof reserveType);
              }}
            >
              <option value="wo_material_requirement">{t("waste.remnants.reserveWo")}</option>
              <option value="sales_order_line">{t("waste.remnants.reserveSo")}</option>
            </select>
          </Field>
          <Field label={t("waste.remnants.reserveRef")} wide>
            <input
              value={reserveId}
              data-testid="remnant-reserve-id"
              onChange={(e) => {
                setReserveId(e.target.value);
              }}
            />
          </Field>
          <Button
            variant="primary"
            size="sm"
            data-testid="remnant-reserve"
            disabled={busy || reserveId.length === 0}
            onClick={() => {
              // Siblings first (the per-verb comment above): a stale refusal
              // from another verb must not outlive this press.
              release.reset();
              reclass.reset();
              scrap.reset();
              reserve.mutate({
                id: row.id,
                body: { reserved_for_type: reserveType, reserved_for_id: reserveId },
              });
            }}
          >
            {t("waste.remnants.reserve")}
          </Button>
        </div>
      ) : null}
      {row.status === "reserved" ? (
        <Button
          size="sm"
          data-testid="remnant-release"
          disabled={busy}
          onClick={() => {
            reserve.reset();
            reclass.reset();
            scrap.reset();
            release.mutate(row.id);
          }}
        >
          {t("waste.remnants.release")}
        </Button>
      ) : null}
      {row.status === "available" || row.status === "reserved" ? (
        <div className="waste-drawer-action">
          <Field label={t("waste.remnants.reclassTo")}>
            <select
              value={classId}
              data-testid="remnant-reclass-select"
              onChange={(e) => {
                setClassId(e.target.value);
              }}
            >
              <option value="">{t("waste.remnants.reclassPick")}</option>
              {(classes.data ?? []).map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {localeLabel(cls.name_i18n, i18n.language) || cls.code}
                </option>
              ))}
            </select>
          </Field>
          <Button
            size="sm"
            data-testid="remnant-reclass"
            disabled={busy || classId.length === 0}
            onClick={() => {
              reserve.reset();
              release.reset();
              scrap.reset();
              reclass.mutate({ id: row.id, priceClassId: classId });
            }}
          >
            {t("waste.remnants.reclass")}
          </Button>
        </div>
      ) : null}
      {row.status === "available" ? (
        <div className="waste-drawer-action">
          <Field label={t("waste.remnants.scrapReason")} wide>
            <input
              value={scrapReason}
              data-testid="remnant-scrap-reason"
              onChange={(e) => {
                setScrapReason(e.target.value);
              }}
            />
          </Field>
          <Button
            variant="danger"
            size="sm"
            data-testid="remnant-scrap"
            disabled={busy || scrapReason.length === 0}
            onClick={() => {
              reserve.reset();
              release.reset();
              reclass.reset();
              scrap.mutate({ id: row.id, reason: scrapReason });
            }}
          >
            {t("waste.remnants.scrap")}
          </Button>
        </div>
      ) : null}
    </>
  );
}
